/*
Description: Read-only note capture for Raintree, used by RaintreeInject and
NewRaintreeInject.

RT Web is a virtual console: the server streams one screen at a time and a SOAP
tab (or a nested sub-tab, or an exam tab) only exists in the DOM while it is the
active one. Capture never navigates to the others. Each poll reads what is on
screen and NoteStore merges it in, so the note accumulates as the user moves
through it — the same model as WebPT 1.0.

Nothing here may click, focus, dispatch events, open pickers, show the overlay
or touch the injectors' scan/fill state; getQuestions is a separate, interactive
path. It also avoids innerText and computed styles so a poll never forces layout.

Shape: getQuestions paths, plus read-only exam measurement columns, so captured
values line up with the generated answers NoteStore compares them to.
  - { [section]: { [subsection]: { [field]: value } } }, with getQuestions'
    subsection rules: Phase 1 panels, then the active nested sub-tab, then loose
    listboxes, with single-field subsections flattened.
  - An open exam: { [exam title]: <objExamNestPath paths> }.
  - Field labels, the new integration's Subjective/Assessment panel mapping and
    the exam field specs come from each injector through RaintreeCaptureAdapter,
    so each integration captures the paths its own scan produces.
  - Fields on screen are always emitted (cleared ones as '' or 'false'), so a
    cleared value overwrites the stored one.

A section or exam shows one sub-tab at a time, so NoteStore merges three levels
deep (noteMergeDepthForCapture): section → subsection → field for SOAP tabs,
exam → tab → sub-tab for exams. Whatever is on screen replaces what was stored
at that level; sub-tabs that are not on screen keep their last captured values.
*/

import {
  normalizeKey,
  panelCaptionName,
  tabDataNameToSectionName
} from './RaintreeHelpers.ts';

export const noteMergeDepthForCapture = 3;
export const noteFinalizationSelectorForCapture =
  '[data-widgetname="btnsaveandsignoff"] [role="button"][aria-label="Save and Sign Off"]';
const noteSignOffDialogSelector = `${noteFinalizationSelectorForCapture}, ` +
  '[id="id.EMRLSTEDITMENU_WSIGNP.WidgetAt_0_2"][aria-keyshortcuts="o"]';

// Same SOAP tabs getQuestions reads.
const SOAP_TAB_BASES = new Set([
  'subjective',
  'objective',
  'assess',
  'plan',
  'activities',
  'goals'
]);

const PT_ABBREVIATIONS: Record<string, string> = {
  flex: 'Flexion',
  ext: 'Extension',
  abd: 'Abduction',
  add: 'Adduction',
  er: 'External Rotation',
  ir: 'Internal Rotation',
  ha: 'Horizontal Adduction',
  habd: 'Horizontal Abduction'
};

const ROM_COLUMNS: Record<string, string> = {
  init: 'Initial',
  goal: 'Goal',
  comm: 'Comments',
  last: 'Last',
  current: 'Current'
};

const ROM_NAME = /^[A-Za-z]{2,8}_([A-Za-z]+)_([A-Za-z]+)$/;

/** Matches the injectors' ObjExamFieldSpec. */
export type CaptureExamFieldSpec = {
  nameAttr: string;
  type: 'input' | 'select' | 'memo' | 'listbox' | 'checkbox';
  choices?: string[];
  mainTabName: string;
  subTabName: string | null;
  panelCaption: string;
  movement: string | null;
  column: string;
  listboxHeaders?: string[];
  skipPanelInPath?: boolean;
};

type ExamSpecMap = Map<string, CaptureExamFieldSpec>;

/** The scan's own exam spec builders. All of them only read the DOM. */
export type CaptureExamScanners = {
  panel: (
    panel: Element,
    mainTabName: string,
    subTabName: string | null,
    result: ExamSpecMap,
    captionOverride?: string
  ) => void;
  flat: (
    content: Element,
    mainTabName: string,
    excludeCtrl: Element | null,
    result: ExamSpecMap
  ) => void;
  // async in the injectors, but it never awaits, so it fills `result` before returning.
  listboxes: (
    content: Element,
    mainTabName: string,
    subTabName: string | null,
    result: ExamSpecMap
  ) => unknown;
};

/**
 * The pieces of each injector's scan that decide paths. Everything here is a
 * pure DOM read; the scan's field builders also attach fill callbacks, which
 * capture discards without calling.
 */
export type RaintreeCaptureAdapter = {
  resolveLabel: (el: Element, container: Element, fallback: string) => string;
  hasRomInputs: (panel: Element) => boolean;
  buildRadioScaleField: (
    radios: HTMLInputElement[],
    container: Element,
    fields: Record<string, any>
  ) => void;
  buildPickerField: (
    picker: Element,
    container: Element,
    subsectionName: string,
    fields: Record<string, any>
  ) => void;
  buildCheckboxListboxField: (
    listbox: Element,
    container: Element,
    subsectionName: string,
    fields: Record<string, any>
  ) => boolean;
  examFieldSpecs: (maintab: Element) => ExamSpecMap;
  examNestPath: (spec: CaptureExamFieldSpec) => string[];
  // New integration only: the subsection a narrative panel is registered under
  // ("Mechanism of Onset" → "Subjective"), and where loose fields go once that
  // key is taken.
  narrativeKey?: (caption: string, sectionKey: string) => string | null;
  catchAllKey?: (sectionKey: string) => string;
};

type Structure = Record<string, Record<string, any>>;

// ─── Note identity ──────────────────────────────────────────────────────────

/** The selected chart row stays underneath the note and its sign-off menu. */
export function getNoteKeyForCapture(): string | null {
  const signOff = document.querySelector(noteSignOffDialogSelector);
  const note = findNoteScreen();
  const screen = signOff || note;
  if (!screen) return null;
  const selection = selectedChartRowBefore(screen);
  if (!selection) return null;
  if (!signOff && readHeaderLabelValue(note!, 'MR #') !== selection.patient) {
    return null;
  }
  return [
    `host:${window.location.hostname}`,
    `mr:${selection.patient}`,
    `note:${selection.rowId}`
  ].join('|');
}

function selectedChartRowBefore(screen: Element) {
  const lists = Array.from(
    document.querySelectorAll('[data-widgetname="signoffview"]')
  );
  const list = lists.reverse().find((candidate) =>
    candidate.compareDocumentPosition(screen) & Node.DOCUMENT_POSITION_FOLLOWING
  );
  const chart = list?.closest('.screen');
  const selected = list?.querySelectorAll('tbody tr.selected[data-index]');
  if (!chart || selected?.length !== 1) return null;
  const rowId = selected[0].getAttribute('data-index');
  const patientColumn = Array.from(list!.querySelectorAll('thead th'))
    .findIndex((header) => header.textContent?.trim() === 'Patient');
  const patient = selected[0].querySelectorAll('td')[patientColumn]
    ?.textContent?.trim() || '';
  return rowId && patient ? { rowId, patient } : null;
}

function findNoteScreen(): Element | null {
  // Several screens can be stacked; the topmost note is the last one.
  const tabs = document.querySelectorAll('[data-widgetname="servicetickettab"]');
  const last = tabs[tabs.length - 1];
  if (!last) return null;
  return last.closest('.screen') || document.body;
}

function readHeaderLabelValue(note: Element, caption: string): string {
  // Same layout getPatientInfo reads: the caption label's widget is followed by
  // the widget holding the value.
  const panel = note.querySelector('[data-widgetname="patinfo"]');
  if (!panel) return '';
  const captionLabel = Array.from(panel.querySelectorAll('label')).find(
    (label) => label.textContent?.trim() === caption
  );
  const valueLabel = captionLabel
    ?.closest('.widget')
    ?.nextElementSibling?.querySelector('label');
  return valueLabel?.textContent?.trim() || '';
}

// ─── Note content ───────────────────────────────────────────────────────────

/**
 * Values on screen right now, keyed as described at the top of this file.
 * Returns {} when no note is open.
 */
export function captureRaintreeNote(
  adapter: RaintreeCaptureAdapter
): Record<string, any> {
  const values: Record<string, any> = {};
  if (document.querySelector(noteSignOffDialogSelector)) return values;

  // An open exam covers the note, and getQuestions treats it the same way:
  // the exam is what the user is editing.
  const exam = findOpenExamScreen();
  if (exam) {
    const maintab = exam.querySelector('[data-widgetname="a_maintab"]')!;
    values[examTitle(exam)] = readExam(exam, maintab, adapter);
    return values;
  }

  const note = findNoteScreen();
  const serviceTicketTab = note?.querySelector(
    '[data-widgetname="servicetickettab"]'
  );
  // The first tab control inside servicetickettab is the SOAP strip itself.
  const soapControl = serviceTicketTab?.querySelector('.w-tab-control');
  if (!soapControl) return values;

  const activeTab = soapControl
    .querySelector('.w-tab-control-tabs')
    ?.querySelector('.tab-item.active') as HTMLElement | null;
  const tabName = activeTab?.dataset.name || '';
  if (!SOAP_TAB_BASES.has(tabName.replace(/\d+$/, ''))) return values;

  const content = directChild(soapControl, 'w-tab-control-content');
  if (!content) return values;

  const sectionKey = tabDataNameToSectionName(tabName);
  values[sectionKey] = readSection(content, sectionKey, adapter);
  return values;
}

function findOpenExamScreen(): Element | null {
  const note = findNoteScreen();
  if (!note) return null;
  const screens = Array.from(document.querySelectorAll('.screen.screen-active'));
  for (let i = screens.length - 1; i >= 0; i--) {
    // An exam is stacked above the note. The Patient Chart under the note also
    // has an a_maintab, and its caption carries the patient's name.
    const aboveNote =
      note.compareDocumentPosition(screens[i]) &
      Node.DOCUMENT_POSITION_FOLLOWING;
    if (aboveNote && screens[i].querySelector('[data-widgetname="a_maintab"]')) {
      return screens[i];
    }
  }
  return null;
}

function examTitle(screen: Element): string {
  // Same title sources, tried in the same order, as getQuestions' open-exam scan.
  const sources = [
    '.screen-title',
    '.screen-header h1, .screen-header h2, .screen-header .title',
    '[data-widgetname="a_title"], [data-widgetname="a_header"]'
  ];
  for (const selector of sources) {
    const text = screen.querySelector(selector)?.textContent?.trim();
    if (text) return text;
  }
  return screen.getAttribute('aria-label')?.trim() || 'Objective Exam';
}

function directChild(parent: Element, className: string): Element | null {
  return (
    Array.from(parent.children).find((c) => c.classList.contains(className)) ||
    null
  );
}

/**
 * The active tab's label, read the way the scan reads it: SOAP sub-tabs by
 * aria-label then span text, exam tabs by aria-label, .tab-label/.tab-title,
 * then the tab's own text.
 */
function activeTabLabel(control: Element, exam = false): string {
  const tabs = control.querySelector('.w-tab-control-tabs');
  const tab =
    tabs?.querySelector('.tab-item.active') ||
    tabs?.querySelector('.tab-item[aria-selected="true"]');
  if (!tab) return '';
  return exam ? examTabLabel(tab) : subTabLabel(tab);
}

function subTabLabel(tab: Element): string {
  return (
    tab.getAttribute('aria-label')?.trim() ||
    tab.querySelector('span')?.textContent?.trim() ||
    ''
  );
}

function examTabLabel(tab: Element): string {
  return (
    tab.getAttribute('aria-label')?.trim() ||
    tab.querySelector('.tab-label, .tab-title')?.textContent?.trim() ||
    tab.textContent?.trim() ||
    ''
  );
}

// ─── SOAP sections (mirrors buildAllPanels) ─────────────────────────────────

type SectionScan = {
  sectionKey: string;
  structure: Structure;
  // Narrative keys ("Subjective") a panel has claimed during this read.
  claimed: Set<string>;
  adapter: RaintreeCaptureAdapter;
};

function readSection(
  content: Element,
  sectionKey: string,
  adapter: RaintreeCaptureAdapter
): Record<string, any> {
  const scan: SectionScan = {
    sectionKey,
    structure: {},
    claimed: new Set(),
    adapter
  };
  const processed = new Set<string>();

  // Nested tab widgets are read through their active sub-tab (Phase 2), so
  // Phase 1 leaves their panels alone.
  const nestedWidgets = Array.from(
    content.querySelectorAll('[data-widgetname]')
  ).filter(
    (el) => !el.closest('.w-panel') && el.querySelector('.w-tab-control')
  );
  const inNestedWidget = (el: Element) =>
    nestedWidgets.some((widget) => widget.contains(el));

  // Phase 1: captioned panels; the first panel with a caption wins.
  for (const panel of Array.from(content.querySelectorAll('.w-panel'))) {
    if (inNestedWidget(panel)) continue;
    const name = panelCaptionName(panel);
    if (!name || processed.has(name)) continue;
    processed.add(name);
    readPanelOrRomGrid(panel, scan);
  }

  // Phase 2: the active sub-tab of each nested tab widget.
  for (const widget of nestedWidgets) {
    const control = widget.querySelector('.w-tab-control');
    if (!control || control.closest('.w-panel')) continue;
    const tabName = activeTabLabel(control);
    const skipTab = !tabName || processed.has(tabName);
    // The scan visits every sub-tab, so all their names are taken (Phase 3).
    control
      .querySelectorAll('.w-tab-control-tabs .tab-item')
      .forEach((tab) => processed.add(subTabLabel(tab)));
    if (skipTab) continue;
    const tabContent = control.querySelector('.w-tab-control-content');
    if (!tabContent) continue;

    const tabPanels = new Set<string>();
    for (const panel of Array.from(tabContent.querySelectorAll('.w-panel'))) {
      const name = panelCaptionName(panel);
      if (!name || tabPanels.has(name)) continue;
      tabPanels.add(name);
      readPanelOrRomGrid(panel, scan);
    }
    readPanel(tabContent, scan, tabName);
  }

  // Phase 3: listboxes outside any panel or nested widget.
  for (const listbox of Array.from(content.querySelectorAll('.w-listbox'))) {
    if (listbox.closest('.w-panel') || inNestedWidget(listbox)) continue;
    if (listbox.querySelector('.w-listbox-scroller.disabled')) continue;
    const label = adapter.resolveLabel(listbox, content, sectionKey);
    if (processed.has(label)) continue;
    processed.add(label);
    if (!scan.structure[label]) scan.structure[label] = {};
    readListbox(listbox, content, label, scan.structure[label], adapter);
  }

  // Phase 4: the scan's sweep over the tab's direct widgets.
  const flatContent =
    content.querySelector(':scope > .w-panel-content') || content;
  const hasFlatDirectWidgets = Array.from(
    flatContent.querySelectorAll(':scope > [data-widgetname]')
  ).some(
    (w) =>
      inlinePosition(w, 'top') >= 0 &&
      !w.querySelector('.w-tab-control') &&
      !!w.querySelector(
        '.w-combobox-openview, .w-memo-content, input[type="checkbox"], input[type="radio"]'
      )
  );
  if (!content.querySelector('.w-panel') || hasFlatDirectWidgets) {
    readFlatWidgets(flatContent, scan);
  }

  return emitStructure(scan);
}

/**
 * buildFlatHtsStructure: looks at each direct widget of the tab (panel widgets
 * included) and registers the first memo, combobox, checkbox and radio it holds
 * under the section's own name, or the new integration's catch-all once a
 * narrative panel owns that name.
 */
function readFlatWidgets(content: Element, scan: SectionScan) {
  const { sectionKey: key, structure, claimed, adapter } = scan;
  const effectiveKey =
    claimed.has(key) && adapter.catchAllKey ? adapter.catchAllKey(key) : key;
  if (!structure[effectiveKey]) structure[effectiveKey] = {};
  const fields = structure[effectiveKey];
  // The new integration skips labels a named panel already registered.
  const registered = (label: string) =>
    !!adapter.catchAllKey && registeredElsewhere(structure, effectiveKey, label);

  const allWidgets = Array.from(
    content.querySelectorAll(':scope > [data-widgetname]')
  );
  const widgets = allWidgets.filter((w) => !w.querySelector('.w-tab-control'));
  const visible = widgets.filter((w) => inlinePosition(w, 'top') >= 0);

  const anchors: { text: string; top: number; left: number }[] = [];
  allWidgets.forEach((w) => {
    const labelEl = w.querySelector('.w-label');
    if (!labelEl) return;
    const forId = w.querySelector('label')?.getAttribute('for') || '';
    if (forId && pointsToInput(content, forId)) return;
    const text = labelEl.textContent?.trim() || '';
    if (text) {
      anchors.push({
        text,
        top: inlinePosition(w, 'top'),
        left: inlinePosition(w, 'left')
      });
    }
  });

  visible.forEach((w) => {
    const memo = w.querySelector('.w-memo-content');
    if (!memo) return;
    const label = adapter.resolveLabel(memo, content, key);
    if (!label || label === key || label in fields || registered(label)) return;
    fields[label] = memoText(memo);
  });

  visible.forEach((w) => {
    const select = w.querySelector('.w-combobox-openview') as HTMLSelectElement | null;
    if (!select) return;
    const label = adapter.resolveLabel(select, content, key);
    if (!label || label === key || label in fields || registered(label)) return;
    fields[label] = selectText(select);
  });

  visible.forEach((w) => {
    const box = w.querySelector('input[type="checkbox"]') as HTMLInputElement | null;
    if (!box) return;
    const label =
      box.getAttribute('aria-label')?.trim() ||
      Array.from(content.querySelectorAll(`label[for="${attrValue(box.id)}"]`))
        .map((l) => l.textContent?.replace(/\u00a0/g, ' ').trim() || '')
        .find((t) => t.length > 0) ||
      box.id;
    if (!label || label in fields || registered(label)) return;
    fields[label] = box.checked ? 'true' : 'false';
  });

  const groups = new Map<string, { text: string; checked: boolean }[]>();
  widgets.forEach((w) => {
    const radio = w.querySelector('input[type="radio"]') as HTMLInputElement | null;
    if (!radio) return;
    const top = inlinePosition(w, 'top');
    const left = inlinePosition(w, 'left');
    let best = '';
    let bestScore = Infinity;
    anchors.forEach((a) => {
      if (a.top > top) return;
      const score = top - a.top + Math.abs(a.left - left);
      if (score < bestScore) {
        bestScore = score;
        best = a.text;
      }
    });
    const group = best.replace(/[:\s]+$/, '') || key;
    const text =
      content.querySelector(`label[for="${attrValue(radio.id)}"]`)?.textContent?.trim() ||
      radio.getAttribute('aria-label')?.trim() ||
      '';
    if (!text) return;
    if (!groups.has(group)) groups.set(group, []);
    groups.get(group)!.push({ text, checked: radio.checked });
  });
  groups.forEach((options, group) => {
    fields[group] = options.find((o) => o.checked)?.text || '';
  });
}

/** _isRegisteredElsewhere: the label is a field of some other subsection. */
function registeredElsewhere(
  structure: Structure,
  ownKey: string,
  label: string
): boolean {
  return Object.entries(structure).some(
    ([k, v]) =>
      k !== ownKey && v && typeof v === 'object' && !Array.isArray(v) && label in v
  );
}

function pointsToInput(content: Element, id: string): boolean {
  return !!content.querySelector(
    `input[id="${attrValue(id)}"], select[id="${attrValue(id)}"]`
  );
}

function selectText(select: HTMLSelectElement): string {
  const option =
    select.selectedIndex >= 0 ? select.options[select.selectedIndex] : null;
  return option?.textContent?.trim() || select.value || '';
}

function readPanelOrRomGrid(panel: Element, scan: SectionScan) {
  const name = panelCaptionName(panel);
  if (/^\d{1,2}[-/]\d{1,2}[-/]\d{2,4}$/.test(name) || scan.adapter.hasRomInputs(panel)) {
    readRomGrid(panel, scan.structure);
  } else {
    readPanel(panel, scan);
  }
}

/** buildPanelStructure's subsection naming, then the panel's fields. */
function readPanel(panel: Element, scan: SectionScan, nameOverride?: string) {
  const { sectionKey, structure, claimed, adapter } = scan;
  const subsectionName = nameOverride || panelCaptionName(panel);
  if (!subsectionName || /add\s+cc/i.test(subsectionName)) return;

  const target = adapter.narrativeKey?.(subsectionName, sectionKey) || null;
  const structureKey =
    target && !claimed.has(target) ? target : subsectionName;
  const isMapped = structureKey !== subsectionName;
  if (isMapped) claimed.add(structureKey);
  // A key a narrative panel owns is never merged into by another panel.
  const effectiveKey =
    !isMapped && claimed.has(structureKey)
      ? adapter.catchAllKey?.(sectionKey) || structureKey
      : structureKey;

  if (!structure[effectiveKey]) structure[effectiveKey] = {};
  const content = panel.querySelector(':scope > .w-panel-content') || panel;
  // Catch-all sweeps (the new integration) never re-register a named panel's field.
  const isCatchAll =
    subsectionName === sectionKey || effectiveKey !== structureKey;
  readFields(content, subsectionName, structure[effectiveKey], adapter, {
    // Reading a sub-tab's own content: its panels were read above.
    skip: (el) => !!nameOverride && !!el.closest('.w-panel'),
    skipLabel:
      adapter.catchAllKey && isCatchAll
        ? (label) =>
            label === sectionKey ||
            label === subsectionName ||
            registeredElsewhere(structure, effectiveKey, label)
        : undefined,
    // A narrative panel's first memo is keyed like the panel ("Assessment").
    firstMemoKey: isMapped ? structureKey : undefined
  });
}

type FieldOptions = {
  skip: (el: Element) => boolean;
  skipLabel?: (label: string) => boolean;
  firstMemoKey?: string;
};

/**
 * The fields buildPanelStructure registers, in its order, so a label shared by
 * two fields resolves to the same (last written) one the scan keeps.
 */
function readFields(
  content: Element,
  subsectionName: string,
  fields: Record<string, any>,
  adapter: RaintreeCaptureAdapter,
  { skip, skipLabel, firstMemoKey }: FieldOptions
) {
  const all = (selector: string) =>
    Array.from(content.querySelectorAll(selector)).filter((el) => !skip(el));
  const set = (label: string, value: any) => {
    if (!skipLabel?.(label)) fields[label] = value;
  };

  let memoRenamed = false;
  all('.w-memo-content').forEach((memo) => {
    const label = adapter.resolveLabel(memo, content, subsectionName);
    if (skipLabel?.(label)) return;
    const key = firstMemoKey && !memoRenamed ? firstMemoKey : label;
    if (firstMemoKey) memoRenamed = true;
    fields[key] = memoText(memo);
  });

  all(
    '.w-field-openview input[type="text"], .w-field-openview input[inputmode="numeric"]'
  ).forEach((input) => {
    set(
      adapter.resolveLabel(input, content, subsectionName),
      (input as HTMLInputElement).value || ''
    );
  });

  all('.w-combobox-openview').forEach((el) => {
    set(
      adapter.resolveLabel(el, content, subsectionName),
      selectText(el as HTMLSelectElement)
    );
  });

  all('.w-checkbox input[type="checkbox"]').forEach((el) => {
    // getQuestions skips checkbox widgets parked off-screen above the panel.
    const widget = el.closest('[data-widgetname]');
    if (widget && inlinePosition(widget, 'top') < 0) return;
    set(
      adapter.resolveLabel(el, content, subsectionName),
      (el as HTMLInputElement).checked ? 'true' : 'false'
    );
  });

  const radios = all(
    '.w-radio-button input[type="radio"]'
  ) as HTMLInputElement[];
  if (radios.length > 0) {
    readRadios(content, radios, subsectionName, fields, adapter, skip);
  }

  all('.w-picker').forEach((picker) => {
    copyExistingValues(fields, (scratch) =>
      adapter.buildPickerField(picker, content, subsectionName, scratch)
    );
  });

  all('.w-listbox').forEach((listbox) => {
    if (listbox.querySelector('.w-listbox-scroller.disabled')) return;
    // The exam list opens exams; getQuestions registers nothing for it.
    if (
      listbox.closest('[data-widgetname]')?.getAttribute('data-widgetname') ===
        'objview' ||
      listbox.id === 'objview'
    ) {
      return;
    }
    readListbox(listbox, content, subsectionName, fields, adapter);
  });
}

/** buildPanelStructure's radio grouping; scale-style groups go to the scan's own builder. */
function readRadios(
  content: Element,
  radios: HTMLInputElement[],
  subsectionName: string,
  fields: Record<string, any>,
  adapter: RaintreeCaptureAdapter,
  skip: (el: Element) => boolean
) {
  const anchors: { text: string; top: number; left: number }[] = [];
  content.querySelectorAll('[data-widgetname]').forEach((widget) => {
    if (skip(widget)) return;
    const labelEl = widget.querySelector('.w-label');
    if (!labelEl) return;
    const forId = widget.querySelector('label')?.getAttribute('for') || '';
    if (forId && pointsToInput(content, forId)) return;
    const text = labelEl.textContent?.trim() || '';
    if (!text || /^\d/.test(text)) return;
    anchors.push({
      text,
      top: inlinePosition(widget, 'top'),
      left: inlinePosition(widget, 'left')
    });
  });

  if (anchors.length === 0) {
    copyExistingValues(fields, (scratch) =>
      adapter.buildRadioScaleField(radios, content, scratch)
    );
    return;
  }

  const groups = new Map<string, { text: string; checked: boolean }[]>();
  radios.forEach((radio) => {
    const widget = radio.closest('[data-widgetname]');
    const top = widget ? inlinePosition(widget, 'top') : 0;
    const left = widget ? inlinePosition(widget, 'left') : 0;
    let best = '';
    let bestScore = Infinity;
    anchors.forEach((a) => {
      if (a.top > top) return;
      const score = top - a.top + Math.abs(a.left - left);
      if (score < bestScore) {
        bestScore = score;
        best = a.text;
      }
    });
    const groupLabel = best.replace(/[:\s]+$/, '') || subsectionName;
    const fromLabel = content
      .querySelector(`label[for="${attrValue(radio.id)}"]`)
      ?.textContent?.trim();
    const aria = radio.getAttribute('aria-label')?.trim();
    const text = fromLabel || (aria && /\d+\s*=/.test(aria) ? '' : aria) || '';
    if (!text) return;
    if (!groups.has(groupLabel)) groups.set(groupLabel, []);
    groups.get(groupLabel)!.push({ text, checked: radio.checked });
  });
  groups.forEach((options, label) => {
    fields[label] = options.find((o) => o.checked)?.text || '';
  });
}

/** buildPanelStructure's listbox order: checkbox list, then row list, then add-row list. */
function readListbox(
  listbox: Element,
  container: Element,
  subsectionName: string,
  fields: Record<string, any>,
  adapter: RaintreeCaptureAdapter
) {
  let isCheckboxList = false;
  copyExistingValues(fields, (scratch) => {
    isCheckboxList = adapter.buildCheckboxListboxField(
      listbox,
      container,
      subsectionName,
      scratch
    );
  });
  if (isCheckboxList) return;

  const label = adapter.resolveLabel(listbox, container, subsectionName);
  if (listbox.querySelector('.w-listbox-toolbar-button.mdi-pen')) {
    fields[label] = readListboxRows(listbox);
  } else if (listbox.querySelector('.w-listbox-toolbar-button.mdi-plus-box')) {
    fields[label] = Array.from(listbox.querySelectorAll('tbody tr'))
      .map((row) =>
        Array.from(row.querySelectorAll('td'))
          .map((td) => td.textContent?.trim() || '')
          .filter(Boolean)
          .join(' | ')
      )
      .filter(Boolean)
      .join(', ');
  }
}

/** Rows as { column header: cell text }, the shape buildRowListField reads and Comprehend writes. */
function readListboxRows(
  listbox: Element,
  headers = listboxHeaders(listbox)
): Record<string, string>[] {
  return Array.from(listbox.querySelectorAll('tbody tr'))
    .map((tr) => {
      const cells = Array.from(tr.querySelectorAll('td')).filter(
        (td) =>
          !td.classList.contains('w-listbox-extended-checkbox') &&
          !td.classList.contains('w-listbox-toolbar-cell')
      );
      const row: Record<string, string> = {};
      headers.forEach((header, i) => {
        row[header] = cells[i]?.textContent?.trim() || '';
      });
      return row;
    })
    .filter((row) => Object.values(row).some((v) => v));
}

function listboxHeaders(listbox: Element): string[] {
  // Skip the same header cells the row cells skip, or columns shift.
  return Array.from(listbox.querySelectorAll('thead th'))
    .filter(
      (th) =>
        !th.classList.contains('w-listbox-extended-checkbox') &&
        !th.classList.contains('w-listbox-toolbar-header')
    )
    .map((th) => th.textContent?.trim() || '')
    .filter(Boolean);
}

/** buildRomDatePanelStructure: a ROM grid registers section → movement → column. */
function readRomGrid(panel: Element, structure: Structure) {
  const content = panel.querySelector(':scope > .w-panel-content') || panel;
  const cells = Array.from(
    content.querySelectorAll(
      '.w-field-openview input[type="text"], .w-field-openview input[inputmode="numeric"]'
    )
  ).concat(Array.from(content.querySelectorAll('.w-memo-content')));
  cells.forEach((el) => {
    const match = (el.getAttribute('name')?.trim() || '').match(ROM_NAME);
    const column = match && ROM_COLUMNS[match[1].toLowerCase()];
    if (!match || !column) return;
    const movement = PT_ABBREVIATIONS[match[2].toLowerCase()] ?? match[2];
    if (!structure[movement]) structure[movement] = {};
    if (column in structure[movement]) return;
    structure[movement][column] =
      el instanceof HTMLInputElement ? el.value || '' : memoText(el);
  });
}

/** Runs one of the scan's field builders and keeps only the value it read. */
function copyExistingValues(
  fields: Record<string, any>,
  build: (scratch: Record<string, any>) => void
) {
  const scratch: Record<string, any> = {};
  build(scratch);
  Object.entries(scratch).forEach(([label, def]) => {
    fields[label] = def?.['Existing Value'] ?? '';
  });
}

/** getQuestions' output rules for a section's structure. */
function emitStructure({
  sectionKey,
  structure,
  claimed,
  adapter
}: SectionScan): Record<string, any> {
  const out: Record<string, any> = {};
  Object.entries(structure).forEach(([subsection, fields]) => {
    const entries = Object.entries(fields);
    if (entries.length === 0) return;
    if (
      entries.length === 1 &&
      normalizeKey(entries[0][0]) === normalizeKey(subsection)
    ) {
      out[subsection] = entries[0][1];
      return;
    }
    // The new integration drops the section-named sweep unless a narrative
    // panel owns that name. Keeping it would merge loose widgets into the
    // stored narrative whenever its sub-tab is off screen.
    if (
      adapter.narrativeKey &&
      normalizeKey(subsection) === normalizeKey(sectionKey) &&
      !claimed.has(subsection) &&
      !entries.some(([, value]) => Array.isArray(value))
    ) {
      return;
    }
    out[subsection] = Object.fromEntries(
      // Raintree data-index aliases are fill-path keys, not fields.
      entries.filter(([field]) => !/^[0-9A-Z]{8,}$/.test(field))
    );
  });
  return out;
}

/** A value for a double-quoted attribute selector. */
function attrValue(value: string): string {
  return value.replace(/["\\]/g, '\\$&');
}

function inlinePosition(el: Element, side: 'top' | 'left'): number {
  return parseInt((el as HTMLElement).style[side] || '0', 10) || 0;
}

// ─── Open exam (mirrors scanObjExamFormFields for the tab on screen) ────────

/**
 * scanObjExamFormFields visits every exam tab; this reads only the active main
 * tab and its active sub-tab, through the same per-panel spec builders.
 */
export function collectVisibleExamSpecs(
  maintab: Element,
  scanners: CaptureExamScanners
): ExamSpecMap {
  const result: ExamSpecMap = new Map();
  const mainStrip = maintab.querySelector('.w-tab-control-tabs');
  const outerCtrl = mainStrip?.parentElement?.parentElement;
  if (!outerCtrl) return result;

  const mainTabName = activeTabLabel(outerCtrl, true);
  const tabContent = outerCtrl.querySelector('.w-tab-control-content');
  if (mainTabName && tabContent) {
    const subStrip = tabContent.querySelector('.w-tab-control-tabs');
    const subCtrl = subStrip?.parentElement?.parentElement || null;
    if (subCtrl) {
      const subTabName = activeTabLabel(subCtrl, true);
      const subContent = subCtrl.querySelector('.w-tab-control-content');
      if (subTabName && subContent) {
        const panels = Array.from(subContent.querySelectorAll('.w-panel'));
        if (panels.length > 0) {
          panels.forEach((p) => scanners.panel(p, mainTabName, subTabName, result));
        } else {
          scanners.panel(subContent, mainTabName, subTabName, result, subTabName);
        }
        scanners.listboxes(subContent, mainTabName, subTabName, result);
      }
      Array.from(tabContent.querySelectorAll('.w-panel'))
        .filter((p) => !subCtrl.contains(p))
        .forEach((p) => scanners.panel(p, mainTabName, null, result));
      scanners.flat(tabContent, mainTabName, subCtrl, result);
    } else {
      const panels = Array.from(tabContent.querySelectorAll('.w-panel'));
      if (panels.length > 0) {
        panels.forEach((p) => scanners.panel(p, mainTabName, null, result));
      } else {
        scanners.panel(tabContent, mainTabName, null, result, mainTabName);
      }
      scanners.listboxes(tabContent, mainTabName, null, result);
      scanners.flat(tabContent, mainTabName, null, result);
    }
  }

  // Panels on the exam screen outside its tab control (the scan's '_screen' tab).
  const screen = maintab.closest('.screen');
  Array.from(screen?.querySelectorAll('.w-panel') || [])
    .filter((p) => !maintab.contains(p))
    .forEach((p) => scanners.panel(p, '_screen', null, result));

  return result;
}

function readExam(
  screen: Element,
  maintab: Element,
  adapter: RaintreeCaptureAdapter
): Record<string, any> {
  const exam: Record<string, any> = {};
  adapter.examFieldSpecs(maintab).forEach((spec) => {
    const el = findExamField(screen, spec.nameAttr);
    if (!el) return;
    setPath(exam, adapter.examNestPath(spec), readExamValue(el, spec));
  });
  return exam;
}

/** fillObjExamRow's lookup, kept inside the exam screen. */
function findExamField(screen: Element, nameAttr: string): Element | null {
  if (!nameAttr) return null;
  const base = nameAttr.split('.').pop() || '';
  return (
    screen.querySelector(`[name="${attrValue(nameAttr)}"]`) ||
    screen.querySelector(`[id="${attrValue(nameAttr)}"]`) ||
    (base ? screen.querySelector(`[id$=".${attrValue(base)}"]`) : null)
  );
}

function readExamValue(el: Element, spec: CaptureExamFieldSpec): any {
  if (spec.type === 'listbox') {
    // The exam template numbers listbox rows "0", "1", … rather than using an array.
    const rows = readListboxRows(el, spec.listboxHeaders || listboxHeaders(el));
    return Object.fromEntries(rows.map((row, i) => [String(i), row]));
  }
  if (spec.type === 'memo') return memoText(el);
  if (spec.type === 'checkbox') {
    const box = (
      el instanceof HTMLInputElement ? el : el.querySelector('input[type="checkbox"]')
    ) as HTMLInputElement | null;
    return box?.checked ? 'true' : 'false';
  }
  if (el instanceof HTMLSelectElement) return selectText(el);
  return (el as HTMLInputElement).value || '';
}

function setPath(target: Record<string, any>, path: string[], value: any) {
  let node = target;
  path.slice(0, -1).forEach((key) => {
    if (!node[key] || typeof node[key] !== 'object') node[key] = {};
    node = node[key];
  });
  node[path[path.length - 1]] = value;
}

// ─── Values ─────────────────────────────────────────────────────────────────

/** Text of a memo, one line per paragraph or <br>, without forcing layout. */
function memoText(el: Element): string {
  let text = '';
  const walk = (node: Node) => {
    node.childNodes.forEach((child) => {
      if (child.nodeType === Node.TEXT_NODE) {
        text += child.nodeValue || '';
      } else if (child instanceof Element) {
        if (child.tagName === 'BR') {
          text += '\n';
          return;
        }
        const isBlock = /^(P|DIV|LI|TR|H[1-6])$/.test(child.tagName);
        if (isBlock && text && !text.endsWith('\n')) text += '\n';
        walk(child);
        if (isBlock && !text.endsWith('\n')) text += '\n';
      }
    });
  };
  walk(el);
  return text
    .replace(/ /g, ' ')
    .split('\n')
    .map((line) => line.trim())
    .join('\n')
    .trim();
}
